// ══════════════════════════════════════════════════════════════
// COMPASS – Authentication Initializer
// ══════════════════════════════════════════════════════════════
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take, switchMap } from 'rxjs/operators';
import { AppState } from '../store/app.state';
import { UserActions } from '../store/user/user.actions';
import { selectIsInitialized, selectIsAuthenticated } from '../store/user/user.selectors';
import { selectPermissionsLoaded } from '../store/management-company/management-company.selectors';
import { Logger } from '../services/logger.service';

/**
 * Factory function for APP_INITIALIZER
 * Dispatches auth initialization and waits for it to complete before app starts.
 *
 * For authenticated users, also waits for permissions to be loaded from the API
 * so that guards have the correct permissions available on first navigation.
 */
export function initializeAuth() {
  const store = inject(Store<AppState>);
  const logger = inject(Logger);

  return (): Promise<void> => {
    store.dispatch(UserActions.initializeAuth());

    return new Promise<void>((resolve) => {
      store
        .select(selectIsInitialized)
        .pipe(
          filter((initialized) => initialized),
          take(1),
          switchMap(() =>
            store.select(selectIsAuthenticated).pipe(
              take(1),
              switchMap((isAuthenticated) => {
                if (!isAuthenticated) {
                  // Not authenticated — resolve immediately
                  return [true];
                }
                // Authenticated — wait for permissions to load
                return store.select(selectPermissionsLoaded).pipe(
                  filter((loaded) => loaded),
                  take(1)
                );
              })
            )
          )
        )
        .subscribe({
          next: () => resolve(),
          error: (err) => {
            logger.error('Auth initialization failed', err);
            // Resolve (don't reject) to avoid crashing bootstrap
            resolve();
          },
        });
    });
  };
}
